import { useState, useEffect, useCallback } from 'preact/hooks';
import { api } from '../api/client';
import { SkeletonBar, Button, Select, Input } from '../components/ui';
import { T } from '../styles/tokens';

interface GatewayConfig {
  enabled: boolean;
  mode: 'test' | 'live';
  api_key?: string;
  secret_key?: string;
  webhook_secret: string;
  services: string[];
  countries: CountryRule;
  payment_methods: string[];
}

interface CountryRule {
  type: 'all' | 'all_except' | 'only';
  list: string[];
}

interface TestResult {
  connected: boolean;
  message?: string;
  error?: string;
}

interface Order {
  id: string;
  customer_id: string;
  customer_email: string;
  plan_id: string;
  plan_name: string;
  total_price_usd: number;
  total_price_brl: number | null;
  currency: string;
  payment_gateway: string;
  status: string;
  created_at: string;
}

const COMMON_COUNTRIES = [
  { code: 'BR', name: 'Brasil' }, { code: 'US', name: 'Estados Unidos' },
  { code: 'PT', name: 'Portugal' }, { code: 'ES', name: 'Espanha' },
  { code: 'GB', name: 'Reino Unido' }, { code: 'DE', name: 'Alemanha' },
  { code: 'FR', name: 'Franca' }, { code: 'IT', name: 'Italia' },
  { code: 'CA', name: 'Canada' }, { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' }, { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' }, { code: 'JP', name: 'Japao' },
  { code: 'AU', name: 'Australia' }, { code: 'IN', name: 'India' },
];

const PAYMENT_METHODS = [
  { id: 'credit_card', label: 'Cartao de Credito' },
  { id: 'debit_card', label: 'Cartao de Debito' },
  { id: 'pix', label: 'PIX' },
  { id: 'boleto', label: 'Boleto' },
  { id: 'apple_pay', label: 'Apple Pay' },
  { id: 'google_pay', label: 'Google Pay' },
];

const STATUS_MAP: Record<string, { label: string; bg: string; color: string; border: string }> = {
  pending: { label: 'Pendente', bg: T.textPrimary, color: T.textSecondary, border: `1px solid ${T.border}` },
  pending_payment: { label: 'Pix Gerado', bg: '#f59e0b22', color: T.warning, border: '1px solid #f59e0b33' },
  paid_pending_setup: { label: 'Pago', bg: T.successBg, color: T.success, border: '1px solid #22c55e33' },
  provisioning: { label: 'Provisionando', bg: '#3b82f622', color: T.primary, border: '1px solid #3b82f633' },
  active: { label: 'Ativo', bg: T.successBg, color: T.success, border: '1px solid #22c55e33' },
  failed: { label: 'Falha', bg: T.dangerBg, color: T.danger, border: '1px solid #ef444433' },
  cancelled: { label: 'Cancelado', bg: T.dangerBg, color: T.danger, border: '1px solid #ef444433' },
};

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const d = new Date(dateStr).getTime();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min atras`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h atras`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d atras`;
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

const styles: Record<string, any> = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, padding: '32px 24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  header: { marginBottom: 32 },
  title: { fontSize: 28, fontWeight: 700, color: T.text, margin: 0 },
  subtitle: { fontSize: 14, color: T.textSecondary, marginTop: 6 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(460px, 1fr))', gap: 24 },
  card: { background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 12, padding: 24 },
  cardTitle: { fontSize: 20, fontWeight: 600, color: T.text, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 },
  label: { display: 'block', fontSize: 13, color: T.textSecondary, marginBottom: 6, marginTop: 16 },
  input: { width: '100%', padding: '10px 12px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 14, outline: 'none', boxSizing: 'border-box' as const },
  select: { width: '100%', padding: '10px 12px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 14, outline: 'none' },
  toggle: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 },
  toggleTrack: (on: boolean) => ({ width: 44, height: 24, borderRadius: 12, background: on ? T.success : T.textPrimary, cursor: 'pointer', position: 'relative' as const, transition: 'background 0.2s' }),
  toggleThumb: (on: boolean) => ({ width: 20, height: 20, borderRadius: 10, background: '#fff', position: 'absolute' as const, top: 2, left: on ? 22 : 2, transition: 'left 0.2s' }),
  checkbox: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 14, color: T.text, cursor: 'pointer' },
  btnRow: { display: 'flex', gap: 12, marginTop: 20 },
  btnPrimary: { padding: '10px 20px', background: T.primaryHover, color: T.text, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { padding: '10px 20px', background: T.textPrimary, color: T.text, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 14, cursor: 'pointer' },
  badge: (status: string) => ({
    display: 'inline-block', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
    background: status === 'connected' ? '#16a34a22' : status === 'error' ? '#dc262622' : '#33333366',
    color: status === 'connected' ? T.success : status === 'error' ? T.danger : T.textSecondary,
    border: `1px solid ${status === 'connected' ? `${T.success}33` : status === `error` ? `${T.danger}33` : `#44444466`}`,
  }),
  testResult: (ok: boolean) => ({ marginTop: 12, padding: '10px 14px', borderRadius: 8, fontSize: 13, background: ok ? '#16a34a18' : '#dc262618', color: ok ? T.success : T.danger, border: `1px solid ${ok ? `${T.success}33` : `${T.danger}33`}` }),
  inputWrap: { position: 'relative' as const },
  showBtn: { position: 'absolute' as const, right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: T.textSecondary, cursor: 'pointer', fontSize: 12, padding: '4px 6px' },
  section: { marginTop: 20, padding: '16px', background: T.bg, borderRadius: 8, border: '1px solid #2a2a2a' },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 10 },
  tag: (active: boolean) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 500,
    background: active ? '#2563eb22' : T.bg, color: active ? T.primary : T.textMuted,
    border: `1px solid ${active ? '#2563eb44' : T.textPrimary}`, cursor: 'pointer', marginRight: 6, marginBottom: 6, transition: 'all 0.15s',
  }),
  countryInfo: { fontSize: 12, color: T.textMuted, marginTop: 8, lineHeight: 1.5 },
  // Tab styles
  tabBar: { display: 'flex', gap: 0, borderBottom: `1px solid ${T.border}`, marginBottom: 24 },
  tab: (active: boolean) => ({
    padding: '12px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: 'none', border: 'none',
    color: active ? '#fff' : T.textSecondary, borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
    transition: 'all 0.15s', marginBottom: -1,
  }),
  // Orders tab styles
  statCard: { background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 10, padding: '16px 20px', flex: 1 },
  statValue: { fontSize: 24, fontWeight: 700, color: T.text },
  statLabel: { fontSize: 12, color: T.textSecondary, marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: { textAlign: 'left' as const, padding: '10px 14px', fontSize: 12, fontWeight: 600, color: T.textSecondary, borderBottom: `1px solid ${T.border}`, textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  td: { padding: '12px 14px', fontSize: 14, color: T.text, borderBottom: '1px solid #1a1a1a' },
  tr: { transition: 'background 0.1s' },
};

function PasswordInput({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [show, setShow] = useState(false);
  return (
    <div style={styles.inputWrap}>
      <Input type={show ? 'text' : 'password'} style={styles.input} value={value} onChange={onChange} placeholder={placeholder} />
      <Button variant="ghost" size="xs" onClick={() => setShow(!show)} style={styles.showBtn}>{show ? 'HIDE' : 'SHOW'}</Button>
    </div>
  );
}

function CountryRuleSelector({ rule: rawRule, onChange }: { rule: CountryRule; onChange: (r: CountryRule) => void }) {
  const rule: CountryRule = { type: rawRule?.type || 'all', list: Array.isArray(rawRule?.list) ? rawRule.list : [] };
  const toggleCountry = (code: string) => {
    const has = rule.list.includes(code);
    const newList = has ? rule.list.filter(c => c !== code) : [...rule.list, code];
    onChange({ ...rule, list: newList });
  };
  const typeLabels: Record<string, string> = { all: 'Global (todos os paises)', all_except: 'Global exceto...', only: 'Apenas estes paises' };
  const summary = rule.type === 'all' ? 'Aceita pagamentos de todos os paises'
    : rule.type === 'all_except' ? `Global exceto: ${rule.list.map(c => COMMON_COUNTRIES.find(cc => cc.code === c)?.name || c).join(', ') || '(nenhum selecionado)'}`
    : `Apenas: ${rule.list.map(c => COMMON_COUNTRIES.find(cc => cc.code === c)?.name || c).join(', ') || '(nenhum selecionado)'}`;

  return (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>Cobertura Geografica</div>
      <Select style={{ ...styles.select, marginBottom: 12 }} value={rule.type} onChange={(v) => onChange({ type: v, list: rule.list })}
        options={Object.entries(typeLabels).map(([k, v]) => ({ value: k, label: v }))} />
      {rule.type !== 'all' && (
        <div style={{ display: 'flex', flexWrap: 'wrap' as const }}>
          {COMMON_COUNTRIES.map(c => <span key={c.code} style={styles.tag(rule.list.includes(c.code))} onClick={() => toggleCountry(c.code)}>{c.name}</span>)}
        </div>
      )}
      <div style={styles.countryInfo}>{summary}</div>
    </div>
  );
}

function PaymentMethodSelector({ methods, onChange }: { methods: string[]; onChange: (m: string[]) => void }) {
  const toggle = (id: string) => {
    const has = methods.includes(id);
    onChange(has ? methods.filter(m => m !== id) : [...methods, id]);
  };
  return (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>Metodos de Pagamento</div>
      <div style={{ display: 'flex', flexWrap: 'wrap' as const }}>
        {PAYMENT_METHODS.map(pm => <span key={pm.id} style={styles.tag(methods.includes(pm.id))} onClick={() => toggle(pm.id)}>{pm.label}</span>)}
      </div>
    </div>
  );
}

function GatewayCard({ name, config, onSave, onTest }: {
  name: string; config: GatewayConfig; onSave: (cfg: GatewayConfig) => Promise<void>; onTest: (cfg: GatewayConfig) => Promise<TestResult>;
}) {
  const [cfg, setCfg] = useState<GatewayConfig>(config);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setCfg(config); }, [config]);

  const isStripe = name === 'Stripe';
  const keyField = isStripe ? 'api_key' : 'secret_key';
  const keyValue = (cfg as any)[keyField] || '';

  const handleSave = async () => { setSaving(true); setSaved(false); try { await onSave(cfg); setSaved(true); setTimeout(() => setSaved(false), 3000); } finally { setSaving(false); } };
  const handleTest = async () => { setTesting(true); setTestResult(null); try { const r = await onTest(cfg); setTestResult(r); } finally { setTesting(false); } };

  const status = !keyValue ? 'none' : (testResult?.connected ? 'connected' : testResult?.error ? 'error' : 'none');

  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>
        <span>{name}</span>
        <span style={styles.badge(status)}>{status === 'connected' ? 'Conectado' : status === 'error' ? 'Erro' : 'Nao configurado'}</span>
      </div>
      <div style={styles.toggle}>
        <div style={styles.toggleTrack(cfg.enabled)} onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })}><div style={styles.toggleThumb(cfg.enabled)} /></div>
        <span style={{ fontSize: 14 }}>{cfg.enabled ? 'Ativo' : 'Inativo'}</span>
      </div>
      <label style={styles.label}>Modo</label>
      <Select style={styles.select} value={cfg.mode} onChange={(v) => setCfg({ ...cfg, mode: v })} options={[{ value: 'test', label: 'Test' }, { value: 'live', label: 'Live' }]} />
      <label style={styles.label}>{isStripe ? 'API Key' : 'Secret Key'}</label>
      <PasswordInput value={keyValue} onChange={(v) => setCfg({ ...cfg, [keyField]: v })} placeholder={isStripe ? 'sk_test_... ou rk_test_...' : 'sk_test_...'} />
      <label style={styles.label}>Webhook Secret</label>
      <PasswordInput value={cfg.webhook_secret || ''} onChange={(v) => setCfg({ ...cfg, webhook_secret: v })} placeholder="whsec_..." />
      <label style={styles.label}>Servicos Conectados</label>
      {['cloud', 'llm_router'].map((svc) => (
        <label key={svc} style={styles.checkbox}>
          <input type="checkbox" checked={cfg.services?.includes(svc)} onChange={() => {
            const services = cfg.services?.includes(svc) ? cfg.services.filter((s) => s !== svc) : [...(cfg.services || []), svc];
            setCfg({ ...cfg, services });
          }} />
          {svc === 'cloud' ? 'Cloud Machines' : 'LLM Router'}
        </label>
      ))}
      <CountryRuleSelector rule={cfg.countries || { type: 'all', list: [] }} onChange={(countries) => setCfg({ ...cfg, countries })} />
      <PaymentMethodSelector methods={cfg.payment_methods || []} onChange={(payment_methods) => setCfg({ ...cfg, payment_methods })} />
      <div style={styles.btnRow}>
        <Button variant="primary" onClick={handleSave} disabled={saving} loading={saving}>{saving ? 'Salvando...' : saved ? 'Salvo!' : 'Salvar'}</Button>
        <Button variant="secondary" onClick={handleTest} disabled={testing} loading={testing}>{testing ? 'Testando...' : 'Testar Conexao'}</Button>
      </div>
      {testResult && <div style={styles.testResult(testResult.connected)}>{testResult.connected ? testResult.message : testResult.error}</div>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_MAP[status] || { label: status, bg: T.textPrimary, color: T.textSecondary, border: `1px solid ${T.border}` };
  return <span style={{ display: 'inline-block', padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: s.bg, color: s.color, border: s.border }}>{s.label}</span>;
}

function OrdersTab() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [statusFilter, setStatusFilter] = useState('all');
  const [gatewayFilter, setGatewayFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (gatewayFilter !== 'all') params.set('gateway', gatewayFilter);
      const r: any = await api.get(`/admin/payment-gateways/orders?${params}`);
      setOrders(r.orders || []);
      setTotal(r.total || 0);
    } catch (e) { console.error('Failed to load orders', e); }
    finally { setLoading(false); }
  }, [page, limit, statusFilter, gatewayFilter]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [statusFilter, gatewayFilter]);

  const paidCount = orders.filter(o => ['paid_pending_setup', 'provisioning', 'active'].includes(o.status)).length;
  const pendingCount = orders.filter(o => ['pending', 'pending_payment'].includes(o.status)).length;
  const failedCount = orders.filter(o => ['failed', 'cancelled'].includes(o.status)).length;

  const totalPages = Math.ceil(total / limit);
  const filterSelect = { ...styles.select, maxWidth: 200, display: 'inline-block', width: 'auto' };

  if (loading && orders.length === 0) return (<div style={{ padding: 20 }}>
          <SkeletonBar width="40%" height={20} style={{ marginBottom: 16 }} />
          <SkeletonBar width="100%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="70%" height={14} style={{ marginBottom: 8 }} />
          <SkeletonBar width="85%" height={14} />
        </div>);

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <div style={styles.statCard}><div style={styles.statValue}>{total}</div><div style={styles.statLabel}>Total Pedidos</div></div>
        <div style={styles.statCard}><div style={{ ...styles.statValue, color: T.success }}>{paidCount}</div><div style={styles.statLabel}>Pagos</div></div>
        <div style={styles.statCard}><div style={{ ...styles.statValue, color: T.warning }}>{pendingCount}</div><div style={styles.statLabel}>Pendentes</div></div>
        <div style={styles.statCard}><div style={{ ...styles.statValue, color: T.danger }}>{failedCount}</div><div style={styles.statLabel}>Falhas</div></div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <Select style={filterSelect} value={statusFilter} onChange={(v) => setStatusFilter(v)}
          options={[{ value: 'all', label: 'Todos os status' }, { value: 'pending', label: 'Pendente' }, { value: 'pending_payment', label: 'Pix Gerado' }, { value: 'paid_pending_setup', label: 'Pago' }, { value: 'provisioning', label: 'Provisionando' }, { value: 'active', label: 'Ativo' }, { value: 'failed', label: 'Falha' }, { value: 'cancelled', label: 'Cancelado' }]} />
        <Select style={filterSelect} value={gatewayFilter} onChange={(v) => setGatewayFilter(v)}
          options={[{ value: 'all', label: 'Todos os gateways' }, { value: 'stripe', label: 'Stripe' }, { value: 'pagarme', label: 'Pagar.me' }]} />
      </div>

      {/* Table */}
      {orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: T.textSecondary, fontSize: 14 }}>Nenhum pedido encontrado</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: 80 }}>ID</th>
              <th style={styles.th}>Cliente</th>
              <th style={{ ...styles.th, width: 120 }}>Plano</th>
              <th style={{ ...styles.th, width: 90 }}>Plano (USD)</th>
              <th style={{ ...styles.th, width: 90 }}>Pago</th>
              <th style={{ ...styles.th, width: 100 }}>Gateway</th>
              <th style={{ ...styles.th, width: 140 }}>Status</th>
              <th style={{ ...styles.th, width: 120 }}>Criado</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} style={styles.tr}>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>{o.id?.slice(0, 8)}</td>
                <td style={styles.td}>{o.customer_email || '-'}</td>
                <td style={styles.td}>{o.plan_name || '-'}</td>
                <td style={styles.td}>${Number(o.total_price_usd || 0).toFixed(2)}</td>
                <td style={styles.td}>{o.currency === 'BRL' && o.total_price_brl ? `R$${Number(o.total_price_brl).toFixed(2)}` : `$${Number(o.total_price_usd || 0).toFixed(2)}`}</td>
                <td style={styles.td}><span style={{ color: o.payment_gateway === 'stripe' ? '#635bff' : T.success, fontWeight: 600 }}>{o.payment_gateway || '-'}</span></td>
                <td style={styles.td}><StatusBadge status={o.status} /></td>
                <td style={{ ...styles.td, color: T.textSecondary, fontSize: 12 }}>{o.created_at ? timeAgo(o.created_at) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, fontSize: 13, color: T.textSecondary }}>
          <span>Mostrando {orders.length} de {total} pedidos</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
            <Button variant="secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Proximo</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Recargas (Wallet Topups) Tab ────────────────────────────────

const TOPUP_STATUS: Record<string, { label: string; color: string }> = {
  paid: { label: 'Pago', color: T.success },
  pending_payment: { label: 'Pendente', color: T.warning },
  failed: { label: 'Falha', color: T.danger },
};

function TopupsTab() {
  const [topups, setTopups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const r: any = await api.get('/wallet/admin/topups?limit=100');
        setTopups(r.topups || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = filter === 'all' ? topups : topups.filter(t => t.status === filter);
  const paid = topups.filter(t => t.status === 'paid');
  const pending = topups.filter(t => t.status === 'pending_payment');
  const failed = topups.filter(t => t.status === 'failed');
  const totalUsd = paid.reduce((s, t) => s + (t.amount || 0), 0);

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div></div>;

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <div style={styles.statCard}><div style={styles.statValue}>{topups.length}</div><div style={styles.statLabel}>Total Recargas</div></div>
        <div style={styles.statCard}><div style={{ ...styles.statValue, color: T.success }}>{paid.length}</div><div style={styles.statLabel}>Pagas</div></div>
        <div style={styles.statCard}><div style={{ ...styles.statValue, color: T.warning }}>{pending.length}</div><div style={styles.statLabel}>Pendentes</div></div>
        <div style={styles.statCard}><div style={{ ...styles.statValue, color: T.danger }}>{failed.length}</div><div style={styles.statLabel}>Falhas</div></div>
        <div style={styles.statCard}><div style={{ ...styles.statValue, color: T.accent }}>${totalUsd.toFixed(2)}</div><div style={styles.statLabel}>Total Pago (USD)</div></div>
      </div>

      {/* Filter */}
      <div style={{ marginBottom: 16 }}>
        <Select value={filter} onChange={(v) => setFilter(v)} style={styles.select}
          options={[{ value: 'all', label: 'Todos os status' }, { value: 'paid', label: 'Pagos' }, { value: 'pending_payment', label: 'Pendentes' }, { value: 'failed', label: 'Falhas' }]} />
      </div>

      {/* Table */}
      <table style={styles.table}>
        <thead>
          <tr><th style={styles.th}>ID</th><th style={styles.th}>Cliente</th><th style={styles.th}>Valor (USD)</th><th style={styles.th}>Status</th><th style={styles.th}>Gateway</th><th style={styles.th}>Data</th></tr>
        </thead>
        <tbody>
          {filtered.map(t => {
            const st = TOPUP_STATUS[t.status] || { label: t.status, color: T.textSecondary };
            return (
              <tr key={t.id} style={styles.tr}>
                <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 12 }}>{t.id?.substring(0, 8)}</td>
                <td style={styles.td}>{t.customer_email || t.customer_id?.substring(0, 8)}</td>
                <td style={{ ...styles.td, fontWeight: 700 }}>${t.amount?.toFixed(2)}</td>
                <td style={styles.td}><span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4, background: `${st.color}22`, color: st.color }}>{st.label}</span></td>
                <td style={{ ...styles.td, color: T.accent, fontSize: 12 }}>{t.payment_gateway || 'pagarme'}</td>
                <td style={{ ...styles.td, fontSize: 12, color: T.textSecondary }}>{timeAgo(t.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {filtered.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: T.textMuted }}>Nenhuma recarga encontrada</div>}
    </div>
  );
}

// ─── Saved Cards Tab ─────────────────────────────────────────────

function SavedCardsTab() {
  const [cards, setCards] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r: any = await api.get('/wallet/admin/saved-cards');
        setCards(r.cards || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div></div>;

  // Group by customer
  const byCustomer: Record<string, any[]> = {};
  cards.forEach(c => {
    const key = c.customer_email || c.customer_id;
    if (!byCustomer[key]) byCustomer[key] = [];
    byCustomer[key].push(c);
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <div style={styles.statCard}><div style={styles.statValue}>{cards.length}</div><div style={styles.statLabel}>Total Cartoes</div></div>
        <div style={styles.statCard}><div style={styles.statValue}>{Object.keys(byCustomer).length}</div><div style={styles.statLabel}>Clientes</div></div>
      </div>

      <table style={styles.table}>
        <thead>
          <tr><th style={styles.th}>Cliente</th><th style={styles.th}>Cartao</th><th style={styles.th}>Bandeira</th><th style={styles.th}>Validade</th><th style={styles.th}>Salvo em</th></tr>
        </thead>
        <tbody>
          {cards.map(c => (
            <tr key={c.id} style={styles.tr}>
              <td style={styles.td}>{c.customer_email || c.customer_name || c.customer_id?.substring(0, 8)}</td>
              <td style={{ ...styles.td, fontFamily: 'monospace' }}>**** {c.last4}</td>
              <td style={{ ...styles.td, textTransform: 'uppercase', fontSize: 12, fontWeight: 600 }}>{c.brand}</td>
              <td style={styles.td}>{c.exp_month}/{c.exp_year}</td>
              <td style={{ ...styles.td, fontSize: 12, color: T.textSecondary }}>{timeAgo(c.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {cards.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: T.textMuted }}>Nenhum cartao salvo</div>}
    </div>
  );
}

// ─── Auto-Recharge Tab ───────────────────────────────────────────

function AutoRechargeTab() {
  const [configs, setConfigs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r: any = await api.get('/wallet/admin/auto-recharge');
        setConfigs(r.configs || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 40 }}><div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div></div>;

  const active = configs.filter(c => c.enabled);
  const totalMonthly = active.reduce((s, c) => s + (c.monthly_limit_usd || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <div style={styles.statCard}><div style={styles.statValue}>{configs.length}</div><div style={styles.statLabel}>Total Configurados</div></div>
        <div style={styles.statCard}><div style={{ ...styles.statValue, color: T.success }}>{active.length}</div><div style={styles.statLabel}>Ativos</div></div>
        <div style={styles.statCard}><div style={{ ...styles.statValue, color: T.accent }}>${totalMonthly.toFixed(0)}</div><div style={styles.statLabel}>Limite Mensal Total</div></div>
      </div>

      <table style={styles.table}>
        <thead>
          <tr><th style={styles.th}>Cliente</th><th style={styles.th}>Status</th><th style={styles.th}>Threshold</th><th style={styles.th}>Valor Recarga</th><th style={styles.th}>Desconto</th><th style={styles.th}>Limite Mensal</th><th style={styles.th}>Atualizado</th></tr>
        </thead>
        <tbody>
          {configs.map(c => (
            <tr key={c.id} style={styles.tr}>
              <td style={styles.td}>{c.customer_email || c.customer_name || c.customer_id?.substring(0, 8)}</td>
              <td style={styles.td}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4, background: c.enabled ? T.successBg : T.dangerBg, color: c.enabled ? T.success : T.danger }}>
                  {c.enabled ? 'Ativo' : 'Inativo'}
                </span>
              </td>
              <td style={{ ...styles.td, fontWeight: 600 }}>{'<'} ${c.threshold_usd}</td>
              <td style={{ ...styles.td, fontWeight: 600 }}>${c.recharge_amount_usd}</td>
              <td style={{ ...styles.td, color: T.success }}>{c.discount_pct}%</td>
              <td style={styles.td}>${c.monthly_limit_usd}/mes</td>
              <td style={{ ...styles.td, fontSize: 12, color: T.textSecondary }}>{timeAgo(c.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {configs.length === 0 && <div style={{ textAlign: 'center', padding: 24, color: T.textMuted }}>Nenhum debito automatico configurado</div>}
    </div>
  );
}

export default function AdminPaymentGateways() {
  const [activeTab, setActiveTab] = useState<'config' | 'orders' | 'topups' | 'cards' | 'auto_recharge'>('config');

  const defaultStripe: GatewayConfig = {
    enabled: true, mode: 'test', api_key: '', webhook_secret: '', services: ['cloud', 'llm_router'],
    countries: { type: 'all_except', list: ['BR'] }, payment_methods: ['credit_card', 'apple_pay', 'google_pay'],
  };
  const defaultPagarme: GatewayConfig = {
    enabled: true, mode: 'test', secret_key: '', webhook_secret: '', services: ['cloud', 'llm_router'],
    countries: { type: 'only', list: ['BR'] }, payment_methods: ['credit_card', 'pix'],
  };

  const [stripe, setStripe] = useState<GatewayConfig>(defaultStripe);
  const [pagarme, setPagarme] = useState<GatewayConfig>(defaultPagarme);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r: any = await api.get('/admin/payment-gateways');
      if (r?.raw?.stripe) {
        const s = { ...defaultStripe, ...r.raw.stripe };
        s.countries = { type: s.countries?.type || 'all', list: Array.isArray(s.countries?.list) ? s.countries.list : [] };
        s.payment_methods = Array.isArray(s.payment_methods) ? s.payment_methods : [];
        s.services = Array.isArray(s.services) ? s.services : [];
        setStripe(s);
      }
      if (r?.raw?.pagarme) {
        const p = { ...defaultPagarme, ...r.raw.pagarme };
        p.countries = { type: p.countries?.type || 'all', list: Array.isArray(p.countries?.list) ? p.countries.list : [] };
        p.payment_methods = Array.isArray(p.payment_methods) ? p.payment_methods : [];
        p.services = Array.isArray(p.services) ? p.services : [];
        setPagarme(p);
      }
    } catch (e) { console.error('Failed to load gateways', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveGateway = async (gateway: string, cfg: GatewayConfig) => { await api.put(`/admin/payment-gateways/${gateway}`, cfg); };
  const testGateway = async (gateway: string, cfg: GatewayConfig): Promise<TestResult> => {
    const body = gateway === 'stripe' ? { api_key: cfg.api_key } : { secret_key: cfg.secret_key };
    return await api.post(`/admin/payment-gateways/${gateway}/test`, body) as TestResult;
  };

  if (loading) {
    return <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div></div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Payment Gateways</h1>
        <p style={styles.subtitle}>Central de gateways de pagamento — configure cobertura geografica e metodos por gateway</p>
      </div>

      {/* Tabs */}
      <div style={styles.tabBar}>
        <Button variant={activeTab === 'config' ? 'primary' : 'ghost'} size="sm" onClick={() => setActiveTab('config')}>Configuracao</Button>
        <Button variant={activeTab === 'orders' ? 'primary' : 'ghost'} size="sm" onClick={() => setActiveTab('orders')}>Pedidos</Button>
        <Button variant={activeTab === 'topups' ? 'primary' : 'ghost'} size="sm" onClick={() => setActiveTab('topups')}>Recargas</Button>
        <Button variant={activeTab === 'cards' ? 'primary' : 'ghost'} size="sm" onClick={() => setActiveTab('cards')}>Cartoes Salvos</Button>
        <Button variant={activeTab === 'auto_recharge' ? 'primary' : 'ghost'} size="sm" onClick={() => setActiveTab('auto_recharge')}>Debito Automatico</Button>
      </div>

      {activeTab === 'config' && (
        <div style={styles.grid}>
          <GatewayCard name="Stripe" config={stripe} onSave={(cfg) => saveGateway('stripe', cfg)} onTest={(cfg) => testGateway('stripe', cfg)} />
          <GatewayCard name="Pagar.me" config={pagarme} onSave={(cfg) => saveGateway('pagarme', cfg)} onTest={(cfg) => testGateway('pagarme', cfg)} />
        </div>
      )}

      {activeTab === 'orders' && <OrdersTab />}
      {activeTab === 'topups' && <TopupsTab />}
      {activeTab === 'cards' && <SavedCardsTab />}
      {activeTab === 'auto_recharge' && <AutoRechargeTab />}
    </div>
  );
}
